/**
 * Tastenkürzel je nach Betriebssystem beschriften (Nutzer-Meldung 2026-09-30:
 * „Ein Windows-Nutzer kann mit Apples Cmd nichts anfangen!").
 *
 * Register und Hinweise speichern abstrakte Tasten (`mod`, `alt`, `shift`,
 * `ctrl`, `enter`, `esc`); erst beim Anzeigen wird daraus ⌘ ⌥ ⇧ (Apple) bzw.
 * Strg, Alt, Umschalt (Windows, Linux). `mod` ist die Befehlstaste auf dem Mac
 * und Strg überall sonst — die Handler prüfen dafür `metaKey || ctrlKey`.
 * Alles, was kein Token ist (Buchstaben, Pfeile, „Klick"), bleibt wörtlich.
 */

export type KeyPlatform = 'apple' | 'other';

const MODIFIER_TOKENS = ['mod', 'alt', 'shift', 'ctrl', 'enter', 'esc', 'backspace'] as const;
export type ModifierToken = (typeof MODIFIER_TOKENS)[number];

const LABELS: Record<KeyPlatform, Record<ModifierToken, string>> = {
  apple: { mod: '⌘', alt: '⌥', shift: '⇧', ctrl: '⌃', enter: '↵', esc: 'esc', backspace: '⌫' },
  // Windows-Tastaturen zeigen auf der Rücktaste nur einen Pfeil; das Mac-Zeichen kennt dort kaum jemand.
  other: { mod: 'Strg', alt: 'Alt', shift: 'Umschalt', ctrl: 'Strg', enter: '↵', esc: 'Esc', backspace: 'Rücktaste' },
};

/** Angaben des Browsers zum System — als Parameter, damit die Erkennung testbar bleibt. */
export interface PlatformHints {
  /** `navigator.userAgentData.platform` (nur Chromium): „macOS", „Windows", „Linux" … */
  userAgentDataPlatform?: string;
  /** `navigator.platform`: „MacIntel", „Win32", „iPhone" … (veraltet, aber in Safari/Firefox die einzige Angabe) */
  platform?: string;
  userAgent?: string;
}

/**
 * macOS, iOS und iPadOS. Das iPad meldet sich seit iPadOS 13 als „MacIntel"
 * bzw. „Macintosh" — das ist gewollt: Mit Tastatur hat es ebenfalls ⌘.
 * Bewusst ohne `i`-Flag: „ios" stünde sonst auch in „Kiosk".
 */
const APPLE_PLATFORM = /Mac|macOS|iPhone|iPad|iPod|iOS/;

export function detectKeyPlatform(hints: PlatformHints): KeyPlatform {
  const source = hints.userAgentDataPlatform || hints.platform || hints.userAgent || '';
  return APPLE_PLATFORM.test(source) ? 'apple' : 'other';
}

/** System des aktuellen Browsers. Ohne `navigator` (kein Browser) gilt die Windows-Beschriftung. */
export function currentKeyPlatform(): KeyPlatform {
  if (typeof navigator === 'undefined') return 'other';
  // `userAgentData` fehlt noch in der DOM-Typbibliothek von TypeScript.
  const { userAgentData } = navigator as Navigator & { userAgentData?: { platform?: string } };
  return detectKeyPlatform({
    userAgentDataPlatform: userAgentData?.platform,
    platform: navigator.platform,
    userAgent: navigator.userAgent,
  });
}

export function isModifierToken(key: string): key is ModifierToken {
  return (MODIFIER_TOKENS as readonly string[]).includes(key);
}

/** Beschriftung einer Taste — Token werden übersetzt, alles andere bleibt, wie es ist. */
export function keyLabel(key: string, platform: KeyPlatform): string {
  return isModifierToken(key) ? LABELS[platform][key] : key;
}

export function keyLabels(keys: readonly string[], platform: KeyPlatform): string[] {
  return keys.map((key) => keyLabel(key, platform));
}

/**
 * Kürzel als ein Text — für Tooltips, Menü-Hinweise und Fließtext.
 * Apple wie im Mac-Menü: Symbole direkt hintereinander („⌘K", „⌥⇧T"), vor und
 * nach Wörtern ein Leerzeichen („⌘ Klick"). Sonst wie in Windows-Menüs mit
 * Plus („Strg+K", „Alt+Umschalt+T").
 */
export function formatShortcut(keys: readonly string[], platform: KeyPlatform): string {
  const labels = keyLabels(keys, platform);
  if (platform === 'other') return labels.join('+');
  return labels
    .map((label, index) => {
      const previous = labels[index - 1];
      const isNextToWord = previous !== undefined && (previous.length > 1 || label.length > 1);
      return isNextToWord ? ` ${label}` : label;
    })
    .join('');
}
